import {
  sortValueScanCandidates,
  VALUE_SCAN_PURPOSES,
  type ValueScanCandidate,
  type ValueScanPurpose,
} from '@/shared/camera/value-scanner';

export function splitOcrCandidateText(text: string, purpose: ValueScanPurpose) {
  const normalizedText = text.trim();

  if (!normalizedText) {
    return [];
  }

  const lines = normalizedText.split(/\r?\n/);
  const roughCandidates = [
    ...lines,
    ...lines.flatMap(line => line.split(/\s+/)),
    ...extractPurposeSpecificCandidates(normalizedText, purpose),
  ];
  const candidates = roughCandidates.map(value => normalizeOcrCandidateText(value, purpose)).filter(Boolean);

  return [...new Set(candidates)];
}

export function dedupeOcrCandidates(candidates: ValueScanCandidate[]) {
  const candidateMap = new Map<string, ValueScanCandidate>();

  for (const candidate of candidates) {
    candidateMap.set(candidate.id, candidate);
  }

  return sortValueScanCandidates([...candidateMap.values()]);
}

export function scoreOcrCandidate(baseConfidence: number, rawValue: string, purpose: ValueScanPurpose) {
  const score = baseConfidence + scorePurposeHeuristic(rawValue, purpose);

  return Math.min(1, Math.max(0, score));
}

export function rankOcrCandidates(candidates: ValueScanCandidate[], purpose: ValueScanPurpose) {
  return dedupeOcrCandidates(candidates)
    .filter(candidate => isUsefulOcrCandidate(candidate.value, purpose))
    .slice(0, 12);
}

function extractPurposeSpecificCandidates(text: string, purpose: ValueScanPurpose) {
  switch (purpose) {
    case VALUE_SCAN_PURPOSES.registerCounter:
      return text.match(/\d+(?:[.,]\d+)?/g) ?? [];
    case VALUE_SCAN_PURPOSES.registerObis:
      return text.match(/\d+(?:-\d+)?\.\d+(?:\.\d+)?/g) ?? [];
    case VALUE_SCAN_PURPOSES.registerUnit:
      return text.match(/\b(?:kWh|MWh|GJ|MJ|m3|m³)\b/gi) ?? [];
    case VALUE_SCAN_PURPOSES.serial:
      return text.match(/[A-Z0-9][A-Z0-9./\\:-]{3,}[A-Z0-9]/gi) ?? [];
    default:
      return [];
  }
}

function normalizeOcrCandidateText(value: string, purpose: ValueScanPurpose) {
  const trimmed = value.trim().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');

  if (!trimmed) {
    return '';
  }

  if (purpose === VALUE_SCAN_PURPOSES.serial) {
    return trimmed
      .replace(/[^\p{L}\p{N}./\\:-]+/gu, '')
      .replace(/[\\/|]/g, '1')
      .replace(/[OQ](?=\d)|(?<=\d)[OQ]/gi, '0')
      .toUpperCase();
  }

  return trimmed;
}

function scorePurposeHeuristic(value: string, purpose: ValueScanPurpose) {
  switch (purpose) {
    case VALUE_SCAN_PURPOSES.serial: {
      const alphanumeric = value.replace(/[^A-Z0-9]/gi, '');

      if (alphanumeric.length < 4) return -0.35;
      if (alphanumeric.length >= 8 && /\d/.test(alphanumeric)) return 0.25;
      if (alphanumeric.length >= 6) return 0.12;

      return 0;
    }
    case VALUE_SCAN_PURPOSES.registerCounter:
      return /\d/.test(value) ? 0.2 : -0.25;
    case VALUE_SCAN_PURPOSES.registerObis:
      return /\d+(?:-\d+)?\.\d+/.test(value) ? 0.25 : -0.3;
    case VALUE_SCAN_PURPOSES.registerUnit:
      return /^(?:kWh|MWh|GJ|MJ|m3|m³)$/i.test(value) ? 0.25 : -0.2;
    default:
      return 0;
  }
}

function isUsefulOcrCandidate(value: string, purpose: ValueScanPurpose) {
  switch (purpose) {
    case VALUE_SCAN_PURPOSES.serial:
      return value.replace(/[^A-Z0-9]/gi, '').length >= 4;
    case VALUE_SCAN_PURPOSES.registerCounter:
      return /\d/.test(value);
    case VALUE_SCAN_PURPOSES.registerObis:
      return /\d+(?:-\d+)?\.\d+/.test(value);
    case VALUE_SCAN_PURPOSES.registerUnit:
      return /^(?:kWh|MWh|GJ|MJ|m3|m³)$/i.test(value);
    default:
      return true;
  }
}
